import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

/** Derive BCP-47 language code from URL pathname */
function getLangFromPath(pathname: string): string {
  if (pathname.startsWith("/es")) return "es";
  if (pathname.startsWith("/de")) return "de";
  if (pathname.startsWith("/fr")) return "fr";
  if (pathname.startsWith("/pt-br")) return "pt-BR";
  return "en";
}

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "170+ Free Classroom Tools, Calculators & PDF Utilities | Picamini.online" },
      {
        name: "description",
        content: "170+ free online tools for teachers & students — random name pickers, spin the wheel, team generators, 122+ calculators (percentage, BMI, mortgage), and private PDF tools. No signup, 100% browser-based.",
      },
      { name: "keywords", content: "picamini, picamini.online, free online tools, random name picker, spin the wheel, wheel of names, team generator, online calculators, percentage calculator, mortgage calculator, pdf tools online" },
      { name: "author", content: "Picamini.online" },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "170+ Free Classroom Tools, Calculators & PDF Utilities | Picamini.online" },
      {
        property: "og:description",
        content: "170+ free interactive tools for teachers, students & professionals: spinner wheels, 122+ calculators, and client-side PDF utilities. 100% free and client-side.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "170+ Free Classroom Tools, Calculators & PDF Utilities | Picamini.online" },
      {
        name: "twitter:description",
        content: "170+ free interactive browser tools: random name pickers, spinner wheels, 122+ calculators & PDF utilities.",
      },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "twitter:site", content: "@PicaminiOnline" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "es_ES" },
      { property: "og:locale:alternate", content: "de_DE" },
      { property: "og:locale:alternate", content: "fr_FR" },
      { property: "og:locale:alternate", content: "pt_BR" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon-48.png", sizes: "48x48", type: "image/png" },
      { rel: "icon", href: "/icon-192.png", sizes: "192x192", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          function initAnalytics(){
            gtag('js', new Date());
            gtag('config', 'G-EYF12PSLRC', { 'anonymize_ip': true });
            var s = document.createElement('script');
            s.async = true;
            s.src = 'https://www.googletagmanager.com/gtag/js?id=G-EYF12PSLRC';
            document.head.appendChild(s);
          }
          if ('requestIdleCallback' in window) {
            requestIdleCallback(initAnalytics, { timeout: 2500 });
          } else {
            setTimeout(initAnalytics, 2000);
          }
        `,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://picamini.online/#organization",
          "name": "Picamini.online",
          "url": "https://picamini.online",
          "logo": "https://picamini.online/icon-512.png",
          "email": "support@picamini.online",
          "sameAs": [
            "https://www.eduzuma.com"
          ],
          "description": "Fast, privacy-focused browser tools for teachers, students, event hosts, and facilitators."
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://picamini.online/#website",
          "name": "Picamini.online",
          "url": "https://picamini.online",
          "description": "Free classroom, decision-making, and PDF tools that work in your browser.",
          "inLanguage": ["en", "es", "de", "fr", "pt-BR"],
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://picamini.online/?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          },
          "availableLanguage": [
            { "@type": "Language", "name": "English", "alternateName": "en" },
            { "@type": "Language", "name": "Spanish", "alternateName": "es" },
            { "@type": "Language", "name": "German", "alternateName": "de" },
            { "@type": "Language", "name": "French", "alternateName": "fr" },
            { "@type": "Language", "name": "Portuguese", "alternateName": "pt-BR" }
          ]
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = getLangFromPath(pathname);

  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <div style={{padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif'}}>
            <h1>Picamini.online — Free Classroom Tools, Calculators &amp; PDF Utilities</h1>
            <p>170+ free online tools for teachers &amp; students. Please enable JavaScript to use Picamini.online interactive tools.</p>
            <p><a href="https://picamini.online/about">About Picamini.online</a> · <a href="https://picamini.online/contact">Contact</a></p>
          </div>
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster />
    </QueryClientProvider>
  );
}
